import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ChevronLeft, Navigation, Receipt, TrendingUp, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { isNative, platform } from "@/lib/native";
import {
  initRevenueCat,
  isRevenueCatConfigured,
  getOwnerPremiumOffering,
  pickOwnerPackage,
  describeTrial,
  purchaseOwnerPremium,
  restorePurchases,
} from "@/services/revenuecat";

export default function OwnerUpgrade() {
  // Native (iOS/Android): Apple IAP / Google Play Billing via RevenueCat.
  // Stripe must never be used for this digital subscription inside the apps.
  if (isNative()) return <NativeOwnerUpgrade />;
  return <WebOwnerUpgrade />;
}

function WebOwnerUpgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Owner/driver subscriptions are managed in the mobile app, never via web Stripe.
  if ((user?.role === "owner" || user?.role === "driver") && !isNative()) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <div className="border-b border-white/10">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigate("/owner", { replace: true })} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" /> Back
            </button>
            <div className="font-display text-sm tracking-widest text-zinc-500">SUBSCRIPTION</div>
            <div className="w-12" />
          </div>
        </div>
        <main className="max-w-lg mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
            <Crown className="w-8 h-8 text-[#FFB800]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-4">Manage Your Subscription in the App</h1>
          <p className="text-zinc-400 text-lg mb-10">ShiftQuote subscriptions for drivers are managed through our mobile app.</p>
          <div className="flex flex-col gap-4">
            <a href="https://apps.apple.com/app/shiftquote/id6740527791" target="_blank" rel="noopener noreferrer" className="block w-full py-4 bg-[#FFB800] text-black font-display tracking-wide hover:bg-[#FFB800]/90">
              Download on the App Store
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.shiftquote.app" target="_blank" rel="noopener noreferrer" className="block w-full py-4 bg-[#FF9500] text-black font-display tracking-wide hover:bg-[#FF9500]/90">
              Get it on Google Play
            </a>
          </div>
        </main>
      </div>
    );
  }

  if (user && user.subscription_tier === "owner_premium") {
    navigate("/owner", { replace: true });
    return null;
  }

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/billing/subscription/checkout", {
        origin_url: isNative() ? "https://shiftquote.app" : window.location.origin,
        tier: "owner_premium",
        claim_trial: true,
      });
      if (res.data?.url) {
        if (isNative()) {
          window.open(res.data.url, "_system");
        } else {
          window.location.href = res.data.url;
        }
      } else {
        toast.error("Unable to start checkout. Please try again.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal");
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Unable to open billing portal.");
      }
    } catch (e) {
      toast.error("Failed to open subscription management.");
    } finally {
      setPortalLoading(false);
    }
  };

  const safeBack = () =>
    window.history.length > 2 ? navigate(-1) : navigate("/owner", { replace: true });

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={safeBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" /> Back
          </button>
          <div className="font-display text-sm tracking-widest text-zinc-500">UPGRADE</div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
            <Crown className="w-8 h-8 text-[#FFB800]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-3">Premium</h1>
          <p className="text-zinc-400 text-lg">Unlock the full driver toolkit.</p>
        </div>

        <div className="border border-white/10 bg-[#121214] p-8">
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-heading text-4xl font-black">$3.99</span>
            <span className="text-zinc-500 text-sm">/month</span>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF3B30]/10 flex items-center justify-center">
                <Navigation className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <div>
                <div className="font-semibold text-white">Auto Mile Tracking</div>
                <div className="text-sm text-zinc-500">Log every drive automatically</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 shrink-0 rounded-full bg-[#34C759]/10 flex items-center justify-center">
                <Receipt className="w-4 h-4 text-[#34C759]" />
              </div>
              <div>
                <div className="font-semibold text-white">Receipt Management</div>
                <div className="text-sm text-zinc-500">Snap, tag, and total your expenses</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF9500]/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[#FF9500]" />
              </div>
              <div>
                <div className="font-semibold text-white">Profit Radar</div>
                <div className="text-sm text-zinc-500">Estimate your take-home after every cost</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6 text-sm text-[#FFB800]">
            <Sparkles className="w-4 h-4" />
            <span>7-day free trial included</span>
          </div>

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="btn-primary w-full py-4 text-lg font-display tracking-wide disabled:opacity-50"
          >
            {loading ? "LOADING..." : "Start Free Trial"}
          </button>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="text-sm text-zinc-500 hover:text-zinc-300 underline underline-offset-4 transition-colors disabled:opacity-50"
          >
            {portalLoading ? "Opening..." : "Already subscribed? Manage subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnerFeatureList() {
  return (
    <div className="space-y-4 mb-8">
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF3B30]/10 flex items-center justify-center">
          <Navigation className="w-4 h-4 text-[#FF3B30]" />
        </div>
        <div>
          <div className="font-semibold text-white">Auto Mile Tracking</div>
          <div className="text-sm text-zinc-500">Log every drive automatically</div>
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 shrink-0 rounded-full bg-[#34C759]/10 flex items-center justify-center">
          <Receipt className="w-4 h-4 text-[#34C759]" />
        </div>
        <div>
          <div className="font-semibold text-white">Receipt Management</div>
          <div className="text-sm text-zinc-500">Snap, tag, and total your expenses</div>
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF9500]/10 flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-[#FF9500]" />
        </div>
        <div>
          <div className="font-semibold text-white">Profit Radar</div>
          <div className="text-sm text-zinc-500">Estimate your take-home after every cost</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Native paywall for the $3.99/mo Car Owner plan (entitlement `owner_premium`).
 * Apple IAP on iOS / Google Play Billing on Android, both via RevenueCat.
 */
function NativeOwnerUpgrade() {
  const { user, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [offeringLoading, setOfferingLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");
  const configured = isRevenueCatConfigured();
  const isIOS = platform() === "ios";
  const alreadyPremium = user?.subscription_tier === "owner_premium";

  useEffect(() => {
    if (alreadyPremium) navigate("/owner", { replace: true });
  }, [alreadyPremium, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!configured) { setOfferingLoading(false); return; }
      try {
        if (user?.user_id) await initRevenueCat(user.user_id);
        const offering = await getOwnerPremiumOffering();
        const found = pickOwnerPackage(offering);
        if (cancelled) return;
        setPkg(found);
        if (!found) setError("Subscription isn't available right now. Please try again later.");
      } catch (e) {
        if (!cancelled) setError(e?.message || "Couldn't load subscription details.");
      } finally {
        if (!cancelled) setOfferingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [configured, user?.user_id]);

  // Sync the store receipt to our backend (JWT-authenticated). The RevenueCat
  // webhook (/api/billing/revenuecat/webhook) is server-to-server and will
  // also land, so a failed sync here is recoverable.
  const syncAndUnlock = async (customerInfo) => {
    try {
      await api.post("/billing/iap/sync", { customer_info: customerInfo });
    } catch (e) {
      console.warn("IAP backend sync failed (webhook will reconcile):", e?.message || e);
    }
    setUser((u) => (u ? { ...u, subscription_tier: "owner_premium", premium_source: "revenuecat" } : u));
    try { await refreshUser(); } catch { /* keep optimistic state */ }
    // refreshUser may return stale tier if sync failed; re-assert locally.
    setUser((u) => (u ? { ...u, subscription_tier: "owner_premium" } : u));
  };

  const handlePurchase = async () => {
    setError("");
    setPurchasing(true);
    try {
      const res = await purchaseOwnerPremium(pkg);
      if (res.userCancelled) return;
      if (res.active) {
        await syncAndUnlock(res.customerInfo);
        toast.success("Welcome to Premium! 🎉");
        navigate("/owner", { replace: true });
      } else {
        setError("Purchase completed but Premium isn't active yet. Tap Restore Purchases in a moment.");
      }
    } catch (e) {
      const msg = e?.message || "Purchase failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setError("");
    setRestoring(true);
    try {
      if (user?.user_id) await initRevenueCat(user.user_id);
      const res = await restorePurchases();
      if (res.active) {
        await syncAndUnlock(res.customerInfo);
        toast.success("Purchases restored. Premium is active.");
        navigate("/owner", { replace: true });
      } else {
        const msg = "No active Premium subscription found for this account.";
        setError(msg);
        toast.info(msg);
      }
    } catch (e) {
      const msg = e?.message || "Restore failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  const safeBack = () =>
    window.history.length > 2 ? navigate(-1) : navigate("/owner", { replace: true });

  const priceString = pkg?.product?.priceString || "$3.99";
  const trialText = describeTrial(pkg) || "7-day free trial";
  const busy = purchasing || restoring;
  const canBuy = configured && !!pkg && !offeringLoading && !busy;
  const storeName = isIOS ? "Apple ID" : "Google Play";

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="border-b border-white/10 safe-top">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={safeBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" /> Back
          </button>
          <div className="font-display text-sm tracking-widest text-zinc-500">UPGRADE</div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
            <Crown className="w-8 h-8 text-[#FFB800]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-3">ShiftQuote Premium</h1>
          <p className="text-zinc-400 text-lg">Unlock the full driver toolkit.</p>
        </div>

        <div className="border border-white/10 bg-[#121214] p-8" data-testid="native-owner-paywall">
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-heading text-4xl font-black">{priceString}</span>
            <span className="text-zinc-500 text-sm">/month</span>
          </div>

          <OwnerFeatureList />

          <div className="flex items-center gap-2 mb-6 text-sm text-[#FFB800]">
            <Sparkles className="w-4 h-4" />
            <span>{trialText}, then {priceString}/month</span>
          </div>

          <button
            onClick={handlePurchase}
            disabled={!canBuy}
            data-testid="start-free-trial"
            className="w-full py-4 text-lg font-display tracking-wide bg-[#FFB800] text-black hover:bg-[#FF9500] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {purchasing ? "PROCESSING..." : offeringLoading ? "LOADING..." : "Start Free Trial"}
          </button>

          {!configured && (
            <p className="mt-4 text-sm text-[#FF9500] text-center">
              In-app subscriptions are being set up. Please check back soon.
            </p>
          )}
          {error && (
            <p className="mt-4 text-sm text-[#FF3B30] text-center" role="alert" data-testid="iap-error">
              {error}
            </p>
          )}
        </div>

        <div className="text-center mt-6">
          <button
            onClick={handleRestore}
            disabled={!configured || busy}
            data-testid="restore-purchases"
            className="text-sm text-[#FFB800] hover:text-[#FF9500] underline underline-offset-4 transition-colors disabled:opacity-50"
          >
            {restoring ? "Restoring..." : "Restore Purchases"}
          </button>
        </div>

        <p className="mt-8 text-xs text-zinc-500 text-center leading-relaxed">
          {trialText} for new subscribers. Payment is charged to your {storeName} account when the
          trial ends. Subscription auto-renews at {priceString}/month unless cancelled at least
          24 hours before the end of the current period. Manage or cancel anytime in your
          {isIOS ? " App Store account settings" : " Google Play subscriptions"}.{" "}
          <Link to="/terms" className="underline">Terms of Use</Link> ·{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
